import { Effect, Exit } from "effect";
import type { WorkbenchCore } from "./workbench-core.ts";
import { beginNavigation } from "./workbench-navigation.ts";
import { stationInvalid } from "@workerstation/shared/errors/station";

export const deleteWorkbenchWorkspace = Effect.fn("Frontend.StationWorkbench.delete")(function* (core: WorkbenchCore, id: string) {
  const initial = yield* core.read;
  if (!initial.booted || initial.stationId === null || !(yield* core.active())) return;
  if (!(yield* core.io.confirm(`Delete workerspace ${initial.workspaces.indexOf(id) + 1} (${id.slice(0, 8)}…) from this workerstation?`)) || !(yield* core.active())) return;
  const before = yield* core.read, stationId = initial.stationId, sequence = yield* beginNavigation(core);
  if (!(yield* core.active(sequence))) return;
  yield* core.accepted.join(Effect.gen(function* () {
    if (!(yield* core.active(sequence))) return;
    if (id !== before.workspaceId) {
      const result = yield* Effect.exit(core.station.mutate(stationId, current => Effect.succeed({ ...current, workspaces: current.workspaces.filter(value => value !== id) })));
      if (!(yield* core.active())) return;
      if (Exit.isFailure(result)) return yield* core.io.alert("Failed to update the workerstation session. Workspace deletion was canceled.");
      return yield* core.update(current => ({ ...current, workspaces: result.value.workspaces,
        activeWorkspace: current.navigationSequence === sequence ? result.value.active : current.activeWorkspace }));
    }
    const replacementId = before.workspaces.filter(value => value !== id)[0];
    if (replacementId !== undefined) {
      const loaded = yield* Effect.exit(core.readAccepted(core.api.workspace(replacementId, stationId)));
      if (Exit.isFailure(loaded)) {
        if (yield* core.active()) yield* core.io.alert("Could not load the replacement workerspace. Deletion was canceled.");
        return;
      }
      const selected = (yield* core.read).workspaceId;
      const result = yield* Effect.exit(core.station.mutate(stationId, current => Effect.gen(function* () {
        if (!current.workspaces.includes(id) || !current.workspaces.includes(replacementId)) return current;
        const workspaces = current.workspaces.filter(value => value !== id);
        return { ...current, workspaces, active: (yield* core.read).navigationSequence !== sequence && selected !== null && workspaces.includes(selected) ? selected : replacementId };
      })));
      if (Exit.isFailure(result) || result.value.workspaces.includes(id)) {
        if (yield* core.active()) yield* core.io.alert("Failed to update the workerstation session. Workspace deletion was canceled.");
        return;
      }
      yield* core.pending.discard(yield* core.target(id));
      if (!(yield* core.active())) return;
      yield* core.update(current => ({ ...current, workspaces: result.value.workspaces }));
      if (yield* core.active(sequence)) yield* core.hydrate(yield* core.target(replacementId), loaded.value);
      return;
    }
    const created = yield* core.create(yield* core.io.blank);
    if (!created.persisted) {
      if (yield* core.active()) yield* core.io.alert("Failed to create a replacement workerspace. Deletion was canceled.");
      return;
    }
    const selected = (yield* core.read).workspaceId;
    const result = yield* Effect.exit(core.station.mutate(stationId, current => Effect.gen(function* () {
      const deletionIsCurrent = (yield* core.read).navigationSequence === sequence;
      let workspaces = current.workspaces.filter(value => value !== id);
      if (deletionIsCurrent || workspaces.length === 0) workspaces = workspaces.includes(created.id) ? workspaces : [...workspaces, created.id];
      const first = workspaces[0];
      if (first === undefined) return yield* Effect.fail(stationInvalid("Station membership cannot be empty."));
      const active = !deletionIsCurrent && selected !== null && workspaces.includes(selected) ? selected
        : deletionIsCurrent ? created.id : workspaces.includes(current.active) ? current.active : first;
      return { ...current, workspaces, active };
    })));
    if (Exit.isFailure(result)) {
      if (yield* core.active()) yield* core.io.alert("Failed to update the workerstation session. Replacement deletion was canceled.");
      return;
    }
    yield* core.pending.discard(yield* core.target(id));
    if (!(yield* core.active())) return;
    yield* core.update(current => ({ ...current, workspaces: result.value.workspaces }));
    if (yield* core.active(sequence)) yield* core.hydrate(created.target, created.draft, true);
  }));
});

export const addWorkbenchWorkspace = Effect.fn("Frontend.StationWorkbench.add")(function* (core: WorkbenchCore) {
  const before = yield* core.read;
  if (!before.booted || before.stationId === null || !(yield* core.active())) return;
  const stationId = before.stationId, sequence = yield* beginNavigation(core);
  if (!(yield* core.active(sequence))) return;
  yield* core.accepted.join(Effect.gen(function* () {
    if (!(yield* core.active(sequence))) return;
    const flushed = yield* Effect.exit(core.pending.flush);
    if (Exit.isFailure(flushed)) return yield* core.update(current => ({ ...current, phase: "error" }));
    const created = yield* core.create(yield* core.io.blank);
    if (!created.persisted) {
      if (yield* core.active()) yield* core.io.alert("Failed to create the new workerspace. Please try again.");
      return;
    }
    const result = yield* Effect.exit(core.station.mutate(stationId, current => Effect.gen(function* () {
      return { ...current, workspaces: current.workspaces.includes(created.id) ? current.workspaces : [...current.workspaces, created.id],
        active: (yield* core.read).navigationSequence === sequence ? created.id : current.active };
    })));
    if (!(yield* core.active())) return;
    if (Exit.isFailure(result)) return yield* core.io.alert("Failed to update the workerstation session. New workerspace was not added.");
    yield* core.update(current => ({ ...current, workspaces: result.value.workspaces }));
    if (yield* core.active(sequence)) yield* core.hydrate(created.target, created.draft, true);
  }));
});

export const reorderWorkbenchWorkspaces = Effect.fn("Frontend.StationWorkbench.reorder")(function* (
  core: WorkbenchCore, movedId: string, targetId: string, placeAfterTarget: boolean
) {
  const before = yield* core.read;
  if (before.stationId === null || !before.booted || !(yield* core.active())) return;
  const result = yield* Effect.exit(core.station.mutate(before.stationId, current => Effect.sync(() => {
    if (!current.workspaces.includes(movedId) || !current.workspaces.includes(targetId)) return current;
    const workspaces = current.workspaces.filter(id => id !== movedId), index = workspaces.indexOf(targetId);
    workspaces.splice(index + (placeAfterTarget ? 1 : 0), 0, movedId);
    return { ...current, workspaces };
  })));
  if (!(yield* core.active())) return;
  if (Exit.isFailure(result)) return yield* core.io.alert("Failed to update the workerstation session. Workspace order was not changed.");
  yield* core.update(current => ({ ...current, workspaces: result.value.workspaces }));
});
